import {useEffect, useState} from 'react';

import {
  addDebouncedFocusVisibleRefetch,
  isAbortError,
} from '../../../browserRefetch';
import {getOutlookMailboxUserEmail} from '../../../outlookMailboxEmail';
import {lookupOutlookEmailSync} from '../../../requests/lookupOutlookEmailSync';
import {usePodRequest} from '../../../usePodRequest';
import {normalizeEmailAddress} from './emailNormalization';
import {
  type OutlookRecipient,
  readOutlookMessageFromAddress,
  readOutlookMessageRecipients,
  readOutlookMessageSubject,
} from './useOutlookEmailRecipients';

type MessageLookupFields = {
  internetMessageId: string | null;
  microsoftMessageId: string | null;
  fromAddress: string;
  toAddresses: string[];
  ccAddresses: string[];
  subject: string;
  sentAt: string | null;
};

const getMailboxItem = (): any | null => {
  if (typeof Office === 'undefined') {
    return null;
  }
  const item = Office.context?.mailbox?.item;
  return item?.itemType === Office.MailboxEnums.ItemType.Message ? item : null;
};

// item.to is an array in read mode but a recipient object in compose mode
const isReadModeMessage = (item: any | null): boolean => {
  return Array.isArray(item?.to);
};

const restMessageId = (item: any): string | null => {
  const itemId = item?.itemId;
  if (!itemId || typeof itemId !== 'string') {
    return null;
  }

  try {
    const mailbox = Office.context.mailbox;
    return typeof mailbox.convertToRestId === 'function'
      ? mailbox.convertToRestId(itemId, Office.MailboxEnums.RestVersion.v2_0)
      : itemId;
  } catch {
    return itemId;
  }
};

const sentAtIso = (item: any): string | null => {
  const created = item?.dateTimeCreated;
  return created instanceof Date && !Number.isNaN(created.getTime())
    ? created.toISOString()
    : null;
};

const emailsForField = (
  recipients: OutlookRecipient[],
  field: OutlookRecipient['field']
): string[] => {
  return recipients
    .filter((r) => r.field === field && r.email)
    .map((r) => r.email);
};

const readMessageLookupFields =
  async (): Promise<MessageLookupFields | null> => {
    const item = getMailboxItem();
    if (!isReadModeMessage(item)) {
      return null;
    }

    const [fromAddress, subject, recipients] = await Promise.all([
      readOutlookMessageFromAddress(),
      readOutlookMessageSubject(),
      readOutlookMessageRecipients(),
    ]);

    return {
      internetMessageId: item.internetMessageId?.trim() || null,
      microsoftMessageId: restMessageId(item),
      fromAddress,
      toAddresses: emailsForField(recipients, 'to'),
      ccAddresses: emailsForField(recipients, 'cc'),
      subject,
      sentAt: sentAtIso(item),
    };
  };

export const isIncomingMailboxMessage = async (): Promise<boolean> => {
  const item = getMailboxItem();
  if (!isReadModeMessage(item)) {
    return false;
  }

  const mailboxEmail = getOutlookMailboxUserEmail();
  const fromAddress = await readOutlookMessageFromAddress();
  if (!mailboxEmail || !fromAddress) {
    return false;
  }

  return (
    normalizeEmailAddress(fromAddress) !== normalizeEmailAddress(mailboxEmail)
  );
};

export const useShowInboundSyncButton = (
  agencyId: number
): {show: boolean; isLoading: boolean} => {
  const {request} = usePodRequest();
  const [show, setShow] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let activeController: AbortController | undefined;
    let latestRequestId = 0;
    let cancelled = false;

    const load = () => {
      const requestId = ++latestRequestId;
      activeController?.abort();
      const controller = new AbortController();
      activeController = controller;

      setIsLoading(true);

      void (async () => {
        const isStale = () =>
          cancelled ||
          controller.signal.aborted ||
          requestId !== latestRequestId;

        try {
          const incoming = await isIncomingMailboxMessage();
          if (isStale()) {
            return;
          }

          if (!incoming) {
            setShow(false);
            return;
          }

          const inboxEmailAddress = getOutlookMailboxUserEmail();
          const fields = await readMessageLookupFields();
          if (isStale()) {
            return;
          }

          if (!inboxEmailAddress || !fields) {
            setShow(false);
            return;
          }

          const result = await lookupOutlookEmailSync(
            request,
            {agencyId, inboxEmailAddress, ...fields},
            {signal: controller.signal}
          );

          if (isStale()) {
            return;
          }
          setShow(!result.existing_sync);
        } catch (error) {
          if (!isStale() && !isAbortError(error)) {
            setShow(false);
          }
        } finally {
          if (!isStale()) {
            setIsLoading(false);
          }
        }
      })();
    };

    load();

    const mailbox =
      typeof Office !== 'undefined' ? Office.context?.mailbox : undefined;
    if (mailbox?.addHandlerAsync) {
      mailbox.addHandlerAsync(Office.EventType.ItemChanged, load, () => {});
    }

    const removeRefetchListeners = addDebouncedFocusVisibleRefetch(load);

    return () => {
      cancelled = true;
      activeController?.abort();
      removeRefetchListeners();
      mailbox?.removeHandlerAsync(Office.EventType.ItemChanged, () => {});
    };
  }, [agencyId, request]);

  return {show, isLoading};
};
